<template>
	<div class="location-page-wrapper">
		<div class="navbar-white">
			<Navbar
				:backButton="false"
				:supportButton="false"
				:logoSrc="$logoSrc"
			/>
		</div>
		<div class="location-page desktop-center">
			<div
				class="location-banner"
				:class="{ 'permission-granted': userGrantedPermission }"
			>
				<img src="/images/2k-delivery.png" alt="Delivery Image" />
				<div class="location-banner-bottom">
					<div class="location-banner-title">
						Where Are You Ordering To?
					</div>
					<div
						v-if="!userGrantedPermission"
						class="location-banner-subtitle"
					>
						Enter the business name or address to find your location
					</div>
				</div>
			</div>
			<div class="location-page-inner">
				<transition-expand>
					<div v-if="closestBusinesses.length > 0">
						<h2>2ndKitchen Partners Near You</h2>
						<div class="closest-businesses">
							<div
								class="closest-business"
								v-for="business in closestBusinesses"
								:key="business.id"
								@click="selectNearestBusiness(business)"
							>
								<div
									class="business-banner"
									alt="Business Banner"
									:style="{
										backgroundImage: `url('${business.imageUrl}')`
									}"
								>
									<button
										class="
											button
											button-secondary-dark
											button-auto
										"
									>
										View Menu
									</button>
								</div>
								<h2>{{ business.name }}</h2>
								<div class="business-info">
									{{ business.address }}, {{ business.city }},
									{{ business.state }}
								</div>
							</div>
						</div>
					</div>
				</transition-expand>
				<transition-expand>
					<div
						class="sk-widget location-search-widget"
						v-if="!fetchedClosestBusinesses"
						key="0"
					>
						<div>
							<TypeaheadInput
								:placeholder="'Location (e.g. Burnham Pointe, Hopewell Brewing etc...)'"
								:serializer="typeaheadSerializer"
								:query="query"
								:fetch="fetchBusinesses"
								:showClear="true"
								@selected="selectBusiness"
							>
								<div
									slot="prepend"
									class="location-search-icon"
								>
									<i
										slot="prepend"
										class="sk-icon-search-regular"
									></i>
								</div>
							</TypeaheadInput>
						</div>
					</div>
					<div v-else key="1">
						<h2
							class="location-help-title"
							v-if="closestBusinesses.length > 0"
						>
							Don't See The Location You’re Looking For?
						</h2>
						<h2 class="location-help-title" v-else>
							No 2ndKitchen locations found near you
						</h2>
						<h3>
							Try searching the business name, address or reach
							out to support
						</h3>
						<div class="sk-widget location-search-widget">
							<div>
								<TypeaheadInput
									:placeholder="'Location (e.g. Burnham Pointe, Hopewell Brewing etc...)'"
									:serializer="typeaheadSerializer"
									:query="query"
									:fetch="fetchBusinesses"
									@selected="selectBusiness"
								>
									<div
										slot="prepend"
										class="location-search-icon"
									>
										<i
											slot="prepend"
											class="sk-icon-search-regular"
										></i>
									</div>
								</TypeaheadInput>
							</div>
						</div>
						<a
							class="
								button button-primary button-auto button-support
							"
							href="sms:+13127575580"
						>
							<i class="sk-icon-comment-lines-solid"></i>
							Message Support
						</a>
					</div>
				</transition-expand>
				<div class="location-seperator" v-if="canRequestPosition">
					<div></div>
					<div>or</div>
					<div></div>
				</div>
				<transition-expand>
					<div class="sk-widget" v-if="canRequestPosition">
						<div class="location-map-image">
							<button
								class="button button-primary button-auto"
								@click="openGeolocationModal"
							>
								<i class="sk-icon-location-arrow-solid"></i>
								Use Current Location
							</button>
						</div>
					</div>
				</transition-expand>
			</div>
		</div>
		<GeoLocationModal ref="geolocationModal" @getLocation="getLocation" />
	</div>
</template>

<style scoped>
.location-page-wrapper {
	background: var(--sk-grey-bg);
	height: 100%;
}

.location-page-wrapper >>> .autocomplete-results {
	left: 0;
}

.location-page {
	padding-bottom: 10px;
}

.desktop-center {
	max-width: 700px !important;
}

h2 {
	color: var(--gR-base-80);
	font-size: 14px;
	font-weight: 600;
	margin-bottom: 20px;
	margin-left: 10px;
}

h3 {
	font-size: 12px;
	color: var(--sk-grey3);
	margin-bottom: 20px;
}

h2,
h3 {
	margin-left: 10px;
}

.location-help-title {
	margin-bottom: 8px;
}

.location-banner {
	background: var(--gR-base-80);
	height: 200px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	position: relative;
	padding-bottom: 50px;
	margin-bottom: 70px;
}

.location-banner.permission-granted {
	margin-bottom: 30px;
	border-bottom: 1px solid var(--gR-white2);
}

.location-banner.permission-granted .location-banner-bottom {
	bottom: 0;
}

.location-banner img {
	height: 133px;
}

.location-banner-bottom {
	width: calc(100% - 50px);
	margin: auto 25px 0 25px;
	text-align: center;
	position: absolute;
	bottom: -40px;
	left: 0;
}

.location-banner-title {
	height: 50px;
	line-height: 50px;
	border-top-left-radius: 5px;
	border-top-right-radius: 5px;
	background: var(--gR-white);
	color: var(--gR-base-80);
	font-size: 18px;
	font-weight: 600;
}

.location-banner-subtitle {
	height: 40px;
	line-height: 40px;
	border-bottom-left-radius: 5px;
	border-bottom-right-radius: 5px;
	background: var(--sk-grey-bg);
	color: var(--sk-grey3);
	font-size: 12px;
	border: 1px solid var(--gR-white2);
}

.location-page-inner {
	margin: 0 25px;
}

.location-search-widget {
	overflow: visible;
	transform: none;
	perspective: none;
	height: 80px;
}

.location-search-icon {
	display: flex;
	position: absolute;
	height: 100%;
	left: 0;
	align-items: center;
}

.location-search-icon i {
	margin-left: 25px;
	font-size: 13px;
}

.location-search-widget >>> .sk-typeahead-input {
	padding-left: 59px;
}

.location-seperator {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 20px;
}

.location-seperator > div {
	color: var(--sk-grey2);
}

.location-seperator div:first-child,
.location-seperator div:last-child {
	flex: 1;
	height: 1px;
	background: var(--gR-white2);
}

.location-seperator div:first-child {
	margin-right: 20px;
}

.location-seperator div:last-child {
	margin-left: 20px;
}

.location-map-image {
	height: 150px;
	width: 100%;
	position: relative;
	background-image: url("/images/static-location-map.png");
	background-repeat: no-repeat;
	background-size: 150%;
	background-position: right;
}

.button-support,
.location-map-image .button {
	padding: 10px 15px;
	font-weight: 400;
	font-size: 14px;
}

.button-support i,
.location-map-image .button i {
	font-size: 10px;
	margin-right: 20px;
}

.button-support {
	display: block;
	margin: 0 auto 20px auto;
	width: max-content;
}

.location-map-image .button {
	justify-content: left;
	width: calc(100% - 20px);
	display: flex;
	align-items: center;
	position: absolute;
	bottom: 10px;
	left: 10px;
}

.closest-businesses {
	margin-bottom: 45px;
	display: grid;
	grid-template-columns: 1fr;
	grid-column-gap: 30px;
	grid-row-gap: 30px;
}

.closest-business {
	height: 208px;
}

.closest-business .business-banner {
	height: 160px;
	position: relative;
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	background-color: var(--sk-grey2);
	border-radius: 5px;
}

.closest-business h2 {
	margin-bottom: 5px;
	margin-top: 10px;
}

.closest-business .business-info {
	font-size: 12px;
	color: var(--sk-grey3);
	margin-left: 10px;
}

.closest-business .button-auto {
	padding: 5px 15px;
	font-size: 10px;
	position: absolute;
	top: 8px;
	right: 5px;
}

@media only screen and (min-width: 700px) {
	.location-banner {
		border-radius: 5px;
		margin-top: 20px;
	}

	.closest-businesses {
		grid-template-columns: 1fr 1fr;
	}

	.location-map-image {
		background-size: cover;
		background-position: center;
	}

	.location-map-image .button {
		width: auto;
	}
}
</style>

<style>
.location-page-inner .input-group > .custom-select:not(:first-child),
.location-page-inner .input-group > .form-control:not(:first-child) {
	border: 0;
}

.location-page-inner .input-group {
	display: flex;
	align-items: center;
	border: 1px solid var(--gR-white2);
	border-radius: 5px;
}

.location-page-inner .input-group i {
	margin-right: 12px;
	margin-left: 25px;
	font-size: 13px;
}

.list-group-item-action:active,
.list-group-item-action:hover .list-group-item-action:visited,
.list-group-item.active {
	color: #212529;
	background-color: #e9ecef;
	border-color: #e9ecef;
}
</style>

<script>
import loaderMixin from "@/mixins/loader-mixin"
import api from "@/api"
import Navbar from "@/components/navbars/Navbar.vue"
import TransitionExpand from "@/components/common/TransitionExpand.vue"
import GeoLocationModal from "@/components/modals/GeolocationConfirmationModal.vue"
import TypeaheadInput from "@/components/TypeaheadInput.vue"
import { eventBus } from "@/eventBus"
import { EventBusEvents } from "@/enums/EventBusEvents"
import { NotificationType } from "@/enums/NotificationType"

export default {
	name: "Location",
	mixins: [loaderMixin],
	components: {
		Navbar,
		TypeaheadInput,
		TransitionExpand,
		GeoLocationModal
	},
	data() {
		return {
			query: "",
			closestBusinesses: [],
			fetchedClosestBusinesses: false,
			geolocationPermissionResult: null
		}
	},
	computed: {
		canRequestPosition() {
			if (!this.browserSupportsGeolocation) {
				return false
			}
			if (!this.userGrantedPermission) {
				return true
			}
			return false
		},
		browserSupportsGeolocation() {
			return navigator && navigator.geolocation
		},
		userGrantedPermission() {
			return this.geolocationPermissionResult == "granted"
		}
	},
	methods: {
		typeaheadSerializer(result) {
			return {
				text: result.name
			}
		},
		async fetchBusinesses(query) {
			const response = await api.autocompleteBusinesses(query)
			return response.data.businesses
		},
		selectBusiness(business) {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Location Business Selected",
				data: {
					business_id_selected: business ? business.id : "N/A",
					context: "Search Result"
				}
			})
			if (business) {
				const url = window.location.origin + "/" + business.slug
				window.location.href = url
			} else {
				eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
					message:
						"No location found, please select from the available options",
					type: NotificationType.WARNING
				})
			}
		},
		selectNearestBusiness(business) {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Location Business Selected",
				data: {
					business_id_selected: business.id,
					context: "Geolocation Result"
				}
			})
			const url = window.location.origin + "/" + business.slug
			window.location.href = url
		},
		checkLocationPermission() {
			if (navigator.geolocation && navigator.permissions) {
				navigator.permissions
					.query({ name: "geolocation" })
					.then((result) => {
						this.geolocationPermissionResult = result.state
						if (result.state == "granted") {
							this.getLocation()
						}
					})
			}
		},
		openGeolocationModal() {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Geolocation Button Clicked"
			})
			this.$refs.geolocationModal.open()
		},
		getLocation() {
			if (navigator.geolocation) {
				this.toggleLoader(true)
				navigator.geolocation.getCurrentPosition(
					(position) => {
						if (this.geolocationPermissionResult != "granted") {
							this.$store.dispatch("sendMixpanelEvent", {
								msg: "Geolocation Granted"
							})
						}
						this.showPosition(position)
					},
					(error) => {
						this.toggleLoader(false)
						console.warn(error)
						if (error.code == 1) {
							this.geolocationPermissionResult = "denied"
							this.$store.dispatch("sendMixpanelEvent", {
								msg: "Geolocation Denied"
							})
						}
						this.bugsnag.notify(
							"Failed to get geolocation",
							(event) => {
								event.addMetadata("error", {
									message: error.message
										? error.message
										: error
								})
							}
						)
					}
				)
			}
		},
		showPosition(position) {
			this.geolocationPermissionResult = "granted"
			this.closestBusinesses = []
			api.getNearestBusinesses(
				position.coords.latitude,
				position.coords.longitude
			)
				.then((response) => {
					this.fetchedClosestBusinesses = true
					this.toggleLoader(false)
					if (
						response.status == 200 &&
						response.data.businesses &&
						response.data.businesses.length > 0
					) {
						this.closestBusinesses = response.data.businesses
							.sort((a, b) => a.distance - b.distance)
							.map((businessItem) => {
								businessItem.business.imageUrl =
									process.env.VUE_APP_BASE_IMAGE_URL +
									businessItem.business.slug +
									"/" +
									businessItem.business.img
								return businessItem.business
							})
					}
				})
				.catch(() => {
					this.toggleLoader(false)
					this.fetchedClosestBusinesses = true
				})
		}
	},
	mounted: function () {
		this.checkLocationPermission()
	}
}
</script>


